import { useState } from 'react';
import { motion } from 'framer-motion';
import { Upload, CheckCircle, User, Code } from 'lucide-react';

interface ExerciseSectionProps {
  className?: string;
}

type UserRole = 'student' | 'contributor' | null;

export function ExerciseSection({ className = '' }: ExerciseSectionProps) {
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.endsWith('.txt')) {
      setError('Please upload a .txt file');
      return;
    }

    setFile(uploadedFile);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!file) {
      setError('Please upload a file first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('role', userRole || '');

      const response = await fetch('https://lyfy.ltd/check_exercise.php', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setResult(data.message);
      } else {
        setError(data.message || 'An error occurred');
      }
    } catch (err) {
      setError('Failed to process the file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className={`bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6 ${className}`}
    >
      <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Exercise Section</h2>

      {/* Role Selection */}
      {!userRole && (
        <div className="space-y-4">
          <p className="text-gray-600 dark:text-gray-300">Choose your role:</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => setUserRole('student')}
              className="flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
            >
              <User className="w-6 h-6 text-blue-500" />
              <span className="font-medium text-gray-700 dark:text-gray-200">Student</span>
            </button>
            <button
              onClick={() => setUserRole('contributor')}
              className="flex items-center justify-center gap-3 p-4 rounded-xl border-2 border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
            >
              <Code className="w-6 h-6 text-green-500" />
              <span className="font-medium text-gray-700 dark:text-gray-200">Contributor</span>
            </button>
          </div>
        </div>
      )}

      {/* File Upload Section */}
      {userRole && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-gray-600 dark:text-gray-300">
              {userRole === 'student' 
                ? 'Upload your solution file:' 
                : 'Upload exercise file:'}
            </p>
            <button
              onClick={() => setUserRole(null)}
              className="text-sm text-blue-500 hover:text-blue-600 dark:text-blue-400"
            >
              Change Role
            </button>
          </div>

          <div className="relative">
            <input
              type="file"
              accept=".txt"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer block w-full px-4 py-6 bg-gray-50 dark:bg-gray-700 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-all duration-200"
            >
              <div className="flex flex-col items-center justify-center">
                <Upload className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" />
                <span className="text-gray-600 dark:text-gray-400">
                  {file ? file.name : 'Click to upload .txt file'}
                </span>
              </div>
            </label>
          </div>

          {error && (
            <p className="text-red-500 dark:text-red-400 text-sm">{error}</p>
          )}

          {userRole === 'student' && (
            <button
              onClick={handleSubmit}
              disabled={!file || loading}
              className={`w-full py-3 px-4 rounded-xl font-medium text-white transition-all duration-200 
                ${loading 
                  ? 'bg-gray-400 cursor-not-allowed' 
                  : 'bg-blue-500 hover:bg-blue-600'}`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Checking...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Check My Answers
                </span>
              )}
            </button>
          )}

          {result && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-xl">
              {result}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
